import type { AreaItem } from "./area/AreaItem";
import type { ConditionItem } from "./condition/ConditionItem";
import type { EffectItem } from "./effect/EffectItem";
import type { TargetItem } from "./target/TargetItem";
import type { MomentumItem } from "./momentum/MomentumItem";
import type { ImageItem } from "./image/ImageItem";
import type { IncidentItem } from "./incident/IncidentItem";
import type { IncidentTriggerItem } from "./incidentTrigger/IncidentTriggerItem";
import type { IndicatorItem } from "./indicator/IndicatorItem";
import type { ActionItem } from "./action/ActionItem";
import type { RelationItem } from "./relation/RelationItem";
import type { RequirementItem } from "./requirement/RequirementItem";
import type { ResourceItem } from "./resource/ResourceItem";
import type { ScenarioEndItem } from "./scenarioEnd/ScenarioEndItem";
import type { ScenarioItem } from "./scenario/ScenarioItem";
import type { SimulationItem } from "./simulation/SimulationItem";
import type { StakeholderItem } from "./stakeholder/StakeholderItem";
import type { StoreItem } from "./StoreItem";
import type { TemplateItem } from "./template/TemplateItem";
import type { WayItem } from "./way/WayItem";
import type { IndexItem } from "./index/IndexItem";
import type { FlowItem } from "./flow/FlowItem";
import type { FlowBranchItem } from "./flow/FlowBranchItem";
import type { ImpactMatrix } from "./impactMatrix/ImpactMatrix";
import type { SceneNodeItem } from "./scene/SceneNodeItem";
import type { ChartItem } from "./charts/ChartItem";
import type { SceneItem } from "./scene/SceneItem";
import type { SceneRelationSettingsItem } from "./relation/sceneRelationSettings.store";

export type ItemType =
  | "area"
  | "chart"
  | "condition"
  | "effect"
  | "target"
  | "momentum"
  | "flow"
  | "flowBranch"
  | "image"
  | "impactMatrix"
  | "incident"
  | "incidentTrigger"
  | "index"
  | "indicator"
  | "action"
  | "relation"
  | "requirement"
  | "resource"
  | "scenario"
  | "scenarioEnd"
  | "simulation"
  | "stakeholder"
  | "template"
  | "way"
  | "sceneNode"
  | "scene"
  | "sceneRelationSettings";

export type ItemOfStore<T extends ItemType> = T extends "area"
  ? AreaItem
  : T extends "condition"
  ? ConditionItem
  : T extends "chart"
  ? ChartItem
  : T extends "effect"
  ? EffectItem
  : T extends "target"
  ? TargetItem
  : T extends "momentum"
  ? MomentumItem
  : T extends "flow"
  ? FlowItem
  : T extends "flowBranch"
  ? FlowBranchItem
  : T extends "image"
  ? ImageItem
  : T extends "impactMatrix"
  ? ImpactMatrix
  : T extends "incident"
  ? IncidentItem
  : T extends "incidentTrigger"
  ? IncidentTriggerItem
  : T extends "indicator"
  ? IndicatorItem
  : T extends "index"
  ? IndexItem
  : T extends "action"
  ? ActionItem
  : T extends "relation"
  ? RelationItem
  : T extends "requirement"
  ? RequirementItem
  : T extends "resource"
  ? ResourceItem
  : T extends "scenario"
  ? ScenarioItem
  : T extends "scenarioEnd"
  ? ScenarioEndItem
  : T extends "simulation"
  ? SimulationItem
  : T extends "stakeholder"
  ? StakeholderItem
  : T extends "template"
  ? TemplateItem
  : T extends "sceneNode"
  ? SceneNodeItem
  : T extends "way"
  ? WayItem
  : T extends "scene"
  ? SceneItem
  : T extends "sceneRelationSettings"
  ? SceneRelationSettingsItem
  : never;

export function isStoreItemOfType<T extends ItemType>(
  storeItem: StoreItem,
  type: T
): storeItem is ItemOfStore<T> {
  return storeItem.type == type;
}

export type ItemWithTitle = {
  [K in ItemType]: ItemOfStore<K> extends { title: string | null } ? K : never
}[ItemType];
